import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import DatePicker from '../../ui/DatePicker';
import TimePicker from '../../ui/TimePicker';
import AddressAutocomplete from '../../ui/AddressAutocomplete';
import { toast } from '../../ui/Toast';
import { Button } from '../../ui/Button';
import { MapPin, Ticket, Train } from '../../ui/Icon';
import { bookingsApi } from '../../../api/bookings.api';
import { useDocumentExtraction, uploadStagedFiles } from '../../../hooks/useDocumentExtraction';
import type { Booking, BookingType } from '../../../types';
import { DocumentAttachments } from './DocumentAttachments';
import { ReminderField } from './ReminderField';
import { ExtractingPlaceholder } from './ExtractingPlaceholder';
import { FIELD_LABEL } from './documentFiles';

// Not exported: a non-component export from this file would break Fast Refresh,
// and nothing outside the form needs it.
const BOOKING_TYPE_META: Record<BookingType, { icon: React.ElementType; color: string }> = {
  TRANSPORT:  { icon: Train,   color: 'text-indigo-500' },
  EVENT:      { icon: Ticket,  color: 'text-violet-500' },
  ACTIVITY:   { icon: MapPin,  color: 'text-orange-500' },
  RESTAURANT: { icon: Ticket,  color: 'text-rose-500'   },
  OTHER:      { icon: Ticket,  color: 'text-slate-500'  },
};

interface Props {
  tripId: string;
  tripStartDate?: string;
  initial?: Booking;
  initialFiles?: File[];
  defaultType?: BookingType;
  onSaved: (b: Booking) => void;
  onCancel: () => void;
}

export function BookingForm({
  tripId, tripStartDate, initial, initialFiles, defaultType = 'EVENT', onSaved, onCancel,
}: Props) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const err = (bad: boolean) => bad ? ' input-error' : '';

  const [v, setV] = useState({
    type: initial?.type ?? defaultType,
    title: initial?.title ?? '',
    date: initial?.date ? initial.date.slice(0, 10) : '',
    endDate: initial?.endDate ? initial.endDate.slice(0, 10) : '',
    startTime: initial?.startTime ?? '',
    endTime: initial?.endTime ?? '',
    venue: initial?.venue ?? '',
    address: initial?.address ?? '',
    reminderMinutes: (initial?.reminderMinutes ?? null) as number | null,
    notes: initial?.notes ?? '',
  });

  const files = useDocumentExtraction({
    initialFiles,
    extract: fs => bookingsApi.extract(tripId, fs),
    onExtracted: extracted => setV(x => ({
      // The Transport section always stays TRANSPORT regardless of what the AI
      // guessed; elsewhere, accept the AI's category unless it mistakenly said TRANSPORT.
      type: x.type === 'TRANSPORT'
        ? 'TRANSPORT'
        : (extracted.type && extracted.type !== 'TRANSPORT' ? extracted.type : x.type),
      title:     extracted.title || x.title,
      date:      extracted.date.slice(0, 10),
      endDate:   extracted.endDate?.slice(0, 10) ?? x.endDate,
      startTime: extracted.startTime ?? x.startTime,
      endTime:   extracted.endTime   ?? x.endTime,
      venue:     extracted.venue     ?? x.venue,
      address:   extracted.address   ?? x.address,
      reminderMinutes: x.reminderMinutes,
      notes: extracted.bookingRef
        ? [x.notes, `Ref: ${extracted.bookingRef}`].filter(Boolean).join('\n')
        : x.notes,
    })),
  });

  // Plain string compare: both are YYYY-MM-DD, where lexicographic order is
  // chronological.
  const endBeforeStart = !!v.endDate && !!v.date && v.endDate < v.date;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitted(true);
    if (!v.title.trim() || !v.date || endBeforeStart) return;

    setBusy(true);
    try {
      const payload = {
        ...v,
        date: new Date(v.date).toISOString(),
        // Same day is not a range — store it as the single-day booking it is, so
        // the itinerary doesn't stack two cards on one day.
        endDate: v.endDate && v.endDate > v.date ? new Date(v.endDate).toISOString() : null,
        startTime: v.startTime || undefined,
        endTime: v.endTime || undefined,
        reminderMinutes: v.reminderMinutes ?? undefined,
      };
      const result = initial
        ? await bookingsApi.update(tripId, initial.id, payload)
        : await bookingsApi.create(tripId, payload);

      const docs = await uploadStagedFiles(
        files.stagedFiles,
        file => bookingsApi.uploadDocument(tripId, result.id, file),
        result.documents ?? [],
        t,
      );
      onSaved({ ...result, documents: docs });
    } catch {
      toast(t('bookings.toast.saveFailed'), 'error');
    } finally {
      setBusy(false);
    }
  }

  if (files.extracting) return <ExtractingPlaceholder />;

  return (
    <form onSubmit={submit} className="space-y-3">
      {v.type !== 'TRANSPORT' && (
        <div>
          <label className={FIELD_LABEL}>{t('bookings.form.category')}</label>
          <div className="flex flex-wrap gap-2">
            {(Object.keys(BOOKING_TYPE_META) as BookingType[]).filter(bType => bType !== 'TRANSPORT').map(bType => (
              <button key={bType} type="button"
                onClick={() => setV(x => ({ ...x, type: bType }))}
                className={`px-3 py-1.5 rounded-xl text-xs font-semibold border transition-colors cursor-pointer ${
                  v.type === bType ? 'bg-teal-600 text-white border-teal-600' : 'border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:border-teal-200 dark:hover:border-teal-700'
                }`}>
                {t(`bookings.types.${bType}` as Parameters<typeof t>[0])}
              </button>
            ))}
          </div>
        </div>
      )}

      <div>
        <label className={FIELD_LABEL}>{t('bookings.form.title')}</label>
        <input
          className={'input' + err(submitted && !v.title.trim())}
          value={v.title}
          onChange={e => setV(x => ({ ...x, title: e.target.value }))}
          placeholder={t('bookings.form.bookingTitlePlaceholder')}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className={FIELD_LABEL}>{t('bookings.form.startDate')}</label>
          <DatePicker value={v.date} onChange={d => setV(x => ({ ...x, date: d }))} defaultMonth={tripStartDate} error={submitted && !v.date} />
        </div>
        <div>
          <label className={FIELD_LABEL}>{t('bookings.form.startTime')}</label>
          <TimePicker value={v.startTime} onChange={time => setV(x => ({ ...x, startTime: time }))} />
        </div>
        <div>
          <label className={FIELD_LABEL}>{t('bookings.form.endDate')}</label>
          <DatePicker value={v.endDate} onChange={d => setV(x => ({ ...x, endDate: d }))} defaultMonth={v.date || tripStartDate} error={submitted && endBeforeStart} />
          <p className={`mt-1 text-[11px] ${submitted && endBeforeStart ? 'text-red-500' : 'text-slate-400 dark:text-slate-500'}`}>
            {submitted && endBeforeStart ? t('bookings.form.endDateBeforeStart') : t('bookings.form.endDateHint')}
          </p>
        </div>
        <div>
          <label className={FIELD_LABEL}>{t('bookings.form.endTime')}</label>
          <TimePicker value={v.endTime} onChange={time => setV(x => ({ ...x, endTime: time }))} />
        </div>
      </div>

      <div>
        <label className={FIELD_LABEL}>{t('bookings.form.venue')}</label>
        <AddressAutocomplete
          value={v.venue}
          onChange={val => setV(x => ({ ...x, venue: val }))}
          onSelect={d => setV(x => ({ ...x, venue: d.name || d.address || '', address: d.address || x.address }))}
          placeholder={t('bookings.form.venuePlaceholder')}
        />
      </div>

      <ReminderField
        value={v.reminderMinutes}
        onChange={m => setV(x => ({ ...x, reminderMinutes: m }))}
        warn={!v.startTime}
      />

      <div>
        <label className={FIELD_LABEL}>{t('bookings.form.notes')}</label>
        <textarea className="input" value={v.notes} onChange={e => setV(x => ({ ...x, notes: e.target.value }))} rows={2} />
      </div>

      <DocumentAttachments
        staged={files.stagedFiles}
        onAddFiles={files.addFiles}
        onRemoveStaged={files.removeStaged}
        fileInputKey={files.fileInputKey}
        onExtract={files.runExtraction}
        extracting={files.extracting}
      />

      <div className="flex gap-2 pt-1">
        <button type="button" onClick={onCancel} className="flex-1 py-2 rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-semibold text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer">{t('common.cancel')}</button>
        <Button type="submit" variant="cta" loading={busy} className="flex-1 justify-center py-2 text-sm disabled:opacity-50 disabled:pointer-events-none">
          {initial ? t('bookings.form.saveChanges') : t('bookings.form.addBooking')}
        </Button>
      </div>
    </form>
  );
}
